import { useEffect, useRef, useState, useCallback } from 'react';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { LoggerService } from 'services/LoggerService';
import { conf, semiProxyBaseUrl } from 'config';

const logger = LoggerService.getLogger('MeetingHub');

export function useMeetingHub(
  meetingId: string,
  attendeeId: string,
  attendeeName?: string,
  attendeeCompany?: string,
  isPreview = false,
  isLog = false
) {
  const [isConnected, setIsConnected] = useState(false);
  const connectionRef = useRef<HubConnection | null>(null);

  const joinMeetingGroup = useCallback(async () => {
    if (connectionRef.current?.state === HubConnectionState.Connected) {
      try {
        await connectionRef.current.send('JoinMeeting', meetingId);
      } catch (err: any) {
        logger.error('Failed to join meeting group:', err);
      }
    }
  }, [meetingId]);

  useEffect(() => {
    if (!meetingId) return;

    const attendeeNameParam = attendeeName ? `&attendeeName=${encodeURIComponent(attendeeName)}` : '';
    const attendeeCompanyParam = attendeeCompany ? `&attendeeCompany=${encodeURIComponent(attendeeCompany)}` : '';
    const previewParam = isPreview ? '&preview=true' : '';
    const logParam = isLog ? '&log=true' : '';
    const connection = new HubConnectionBuilder()
      .withUrl(
        `${
          semiProxyBaseUrl + conf.proxyEndpoints.ballotVoting
        }?meetingId=${meetingId}&attendeeId=${attendeeId}${attendeeNameParam}${attendeeCompanyParam}${previewParam}${logParam}`,
        {
          transport: HttpTransportType.WebSockets,
          skipNegotiation: false,
        }
      )
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000])
      .configureLogging(LogLevel.Warning)
      .build();

    connection.serverTimeoutInMilliseconds = 60000;
    connection.keepAliveIntervalInMilliseconds = 15000;

    connectionRef.current = connection;

    connection.onreconnecting(() => setIsConnected(false));
    connection.onreconnected(async () => {
      setIsConnected(true);
      await joinMeetingGroup();
    });
    connection.onclose(() => setIsConnected(false));

    connection
      .start()
      .then(async () => {
        setIsConnected(true);
        await joinMeetingGroup();
      })
      .catch((err) => logger.error('Failed to connect:', err));

    return () => {
      if (connection.state === HubConnectionState.Connected) {
        connection
          .invoke('LeaveMeeting')
          .catch((err) => logger.error('Error leaving meeting:', err))
          .finally(() => connection.stop().catch((err) => logger.error('Error stopping connection:', err)));
      } else if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch((err) => logger.error('Error stopping connection:', err));
      }

      connectionRef.current = null;
    };
  }, [meetingId, joinMeetingGroup, attendeeId, attendeeName, attendeeCompany, isPreview, isLog]);

  const callHubMethod = useCallback(
    async (method: string, payload: any = {}) => {
      if (!connectionRef.current || connectionRef.current.state !== HubConnectionState.Connected) {
        logger.warn('Cannot send message, hub not connected.');
        return;
      }

      try {
        const message = { meetingId, ...payload };
        await connectionRef.current.send(method, message);
      } catch (err: any) {
        logger.error(`Error sending message "${method}":`, err);
      }
    },
    [meetingId]
  );

  return {
    connection: connectionRef.current,
    isConnected,
    callHubMethod,
  };
}
